import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { OAuthModule } from 'angular-oauth2-oidc';
import { RouterModule } from '@angular/router';
import { APP_BASE_HREF } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModule, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { AppComponent } from './app.component';
import { ProtectedComponent } from './protected/protected.component';
import { DashboardComponent } from '../app/dashboard/dashboard.component';
import { routing } from './app.routing';
import { LoggedInGuard } from './shared/guards/auth.guard';
import { DataService } from './shared/services/dataService';
import { AnnualReportService } from './shared/services/annualReportService';
import { OfrAnnualReportService } from './shared/services/ofrAnnualReportService';
import { ConfigService } from './shared/services/configService';
import { InterviewDetailsService } from './shared/services/interviewDetailsService';
import { CommentService } from './shared/services/commentService';
import { CaseService } from './shared/services/caseService';
import { UserService } from './shared/services/userService';
import { APP_INITIALIZER } from '@angular/core';
import { AppConfig } from './app.config';
import { DefaultComponent } from './shared/layouts/default.component';
import { LayoutComponent } from './shared/layouts/layout.component';
import { CaseReportComponent } from './dashboard/caseReport/caseReport.component';
import { FilterPipe } from './shared/directives/searchPipe';
import { MainPipe } from './shared/pipes/main-pipe.module';
import { NgxPaginationModule } from 'ngx-pagination';
import { CaseTemplateComponent } from './dashboard/case-template/caseTemplate.component';
import { CaseTemplateFieldComponent } from './dashboard/case-template/caseTemplateField.component';
import { CaseTemplateGroupComponent } from './dashboard/case-template/caseTemplateGroup.component';
import { PdmpComponent } from './dashboard/pdmp/pdmp.component';
import { AlertsComponent } from './dashboard/alerts/alerts.component';
import { ResourceCenterComponent } from './dashboard/resource-center/resource-center.component';
import { AuthenticationService } from './shared/services/authenticationService';
import { LoginRegisterComponent } from './login-register/login-register.component';
import { LogoffComponent } from './logoff/logoff.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { SpinnerService } from './shared/services/spinnerService';
import { EventAggregator } from './shared/utility/EventAggregator';
import { BaseComponent } from './shared/baseClasses/BaseComponent';
import { LoadingModule } from 'ngx-loading';
import { PopupComponent } from './shared/modals/popup.component';
import { CustomAlertComponent } from './shared/modals/custom-alert/custom-alert.component';
import { UserListComponent } from './dashboard/user-list/user-list.component';
import { SortCaretComponent } from './shared/utility/sort-caret/sort-caret.component';
import { EditUserComponent } from './shared/modals/edit-user/editUser.component';
import { ConfirmEmailComponent } from './confirm-email/confirm-email.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { OcmeComponent } from './dashboard/ocme/ocme.component';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { SearchComponent } from './dashboard/search/search.component';
import { TokenInterceptor } from './shared/interceptors/tokenInterceptor';
import { PdmpDetailsComponent } from './dashboard/pdmp/pdmpDetails/pdmpDetails.component';
import { InterviewDetailsComponent } from './dashboard/interview-details/interview-details.component';
import { MaterialModule } from './app.material.module';
import {MatDatepickerModule} from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ResourceCenterService } from './shared/services/resourceCenterService';
import { NotificationService } from 'app/shared/services/notificationService';
import { BannerNotificationManagerComponent } from './banner-notification-manager/banner-notification-manager/banner-notification-manager.component';
import { DetailsComponent } from './dashboard/details/details.component';
import { CaseDetailsComponent } from './dashboard/case-details/case-details.component';

import { CaseCommentThreadComponent } from './case-comment-thread/case-comment-thread.component';
import { SystemConfigurationComponent } from './system-configuration/system-configuration.component';
import { ResourceDocumentsComponent } from './dashboard/resource-documents/resource-documents.component';
import { CaseInfoComponent } from './shared/modals/case-info/case-info.component';
import { GlobalErrorHandler } from './globalErrorHandler';
import { AppInsightService } from './shared/services/app-insight.service';
import { DragDirective } from './shared/directives/dragDrop';
import { ScrollToTopDirective } from './shared/directives/scrollToTop';
import { AnnualReportFormComponent } from './annual-report-form/annual-report-form.component';
import { UIStateService } from './shared/services/uiStateService';
import { RecommendationTrackerComponent } from './recommendation-tracker/recommendation-tracker.component';
import { RecommendationTrackerService } from './shared/services/recommendationTrackerService';
import { CaseListComponent } from './shared/modals/case-list/case-list.component';


@NgModule({
  declarations: [
   AppComponent, ProtectedComponent, DashboardComponent, LayoutComponent, DefaultComponent, CaseReportComponent, FilterPipe,
   CaseTemplateComponent, CaseTemplateFieldComponent, CaseTemplateGroupComponent, PdmpComponent, PdmpDetailsComponent, AlertsComponent,
   ResourceCenterComponent,ResourceDocumentsComponent, LoginRegisterComponent, LogoffComponent, ResetPasswordComponent, PopupComponent, CustomAlertComponent,
   UserListComponent, SortCaretComponent, EditUserComponent, ConfirmEmailComponent, OcmeComponent, SearchComponent,
   InterviewDetailsComponent, BannerNotificationManagerComponent, DetailsComponent, CaseCommentThreadComponent,
   CaseDetailsComponent, SystemConfigurationComponent, CaseInfoComponent, CaseListComponent, DragDirective, ScrollToTopDirective, AnnualReportFormComponent, RecommendationTrackerComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    NgxPaginationModule,
    routing,
    LoadingModule,
    HttpClientModule,
    OAuthModule.forRoot({
      resourceServer: {
        sendAccessToken: true,
        allowedUrls: [
          "api/Account/GetUserProfile"
          ,"api/Account/UpdateUserProfile"
          ,"api/Account/ManageInfo"
          ,"api/Account/SetAccountStatus"
          ,"api/Account/RemoveLogin"
          ,"api/Account/UserInfo"
          ,"api/Account/Logout"
          ,"api/Account/ping"
          ,"api/alerts/"
          ,"api/annualreport"
          ,"api/case"
          ,"api/CaseInterview"
          ,"api/Comments"
          ,"api/config"
          ,"api/Notifications"
          ,"api/ocme"
          ,"api/ofrannualreport"
          ,"api/pdmp"
          ,"api/resource"
          ,"api/template"
          ,"api/user"
          ,"api/recommendation"
        ]
      }
    }),
    MainPipe,
    NgbModule,
    BrowserAnimationsModule,
    //Material Modules Imports
    MaterialModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  providers: [
    LoggedInGuard,
    AuthenticationService,
    DataService,
    AnnualReportService,
    OfrAnnualReportService,
    RecommendationTrackerService,
    ConfigService,
    ResourceCenterService,
    NotificationService,
    InterviewDetailsService,
    CommentService,
    UserService,
    SpinnerService,
    CaseService,
    EventAggregator,
    AppInsightService,
    UIStateService,
    AppConfig, { provide: APP_INITIALIZER, useFactory: init_app, deps: [AppConfig], multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

export function init_app(first_service: AppConfig) {
  // Do initing of services that is required before app loads
  // NOTE: this factory needs to return a function (that then returns a promise)
  return () => first_service.load();  // + any other services...
}

declare global {
  interface Navigator {
      msSaveBlob: (blob: any, defaultName?: string) => boolean
  }
}